// File: oneportal-shell/src/pages/ManageLocations/EditLocationModal.tsx

import { useState } from "react";
import { Button, Input, useToastContext } from "@resideo/blueprint-oneportal";
import { useTranslation } from "react-i18next";
import { updateLocation, type ManagedLocation } from "./manageLocationsService";
import { US_STATES } from "./usStates";
import styles from "./EditLocationModal.module.css";

export interface EditLocationModalProps {
  location: ManagedLocation;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditLocationModal({ location, onClose, onSuccess }: EditLocationModalProps) {
  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const [name, setName] = useState(location.name ?? "");
  const [addressLine1, setAddressLine1] = useState(location.address.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(location.address.addressLine2 ?? "");
  const [city, setCity] = useState(location.address.city ?? "");
  const [state, setState] = useState(location.address.stateProvinceRegionCode ?? "");
  const [zip, setZip] = useState(location.address.zipPostalCode ?? "");

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!name.trim()) {
      setError(t("manageLocations.editModal.nameRequired"));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await updateLocation({
        consumerAccountId: location.consumerAccountId,
        locationId: location.id,
        name: name.trim(),
        address: {
          addressLine1: addressLine1.trim(),
          addressLine2: addressLine2.trim(),
          city: city.trim(),
          stateProvinceRegionCode: state,
          zipPostalCode: zip.trim(),
          countryCode: location.address.countryCode ?? "US",
        },
        geoCoordinate: location.geoCoordinate,
        consumerDeviceIds: location.consumerDeviceIds,
      });

      addToast({
        message: t("manageLocations.editModal.saved"),
        toastType: "Success",
        zIndex: "9999",
      });
      onSuccess();
    } catch {
      setError(t("manageLocations.editModal.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-location-title"
    >
      <div className={styles.modal}>
        <p className={styles.title} id="edit-location-title">
          {t("manageLocations.editModal.title")}
        </p>

        {error && <p className={styles.errorText}>{error}</p>}

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="edit-location-name">
            {t("manageLocations.editModal.nameLabel")}
          </label>
          <Input
            id="edit-location-name"
            name="edit-location-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="edit-location-address1">
            {t("manageLocations.editModal.addressLine1Label")}
          </label>
          <Input
            id="edit-location-address1"
            name="edit-location-address1"
            type="text"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="edit-location-address2">
            {t("manageLocations.editModal.addressLine2Label")}
          </label>
          <Input
            id="edit-location-address2"
            name="edit-location-address2"
            type="text"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
          />
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="edit-location-city">
              {t("manageLocations.editModal.cityLabel")}
            </label>
            <Input
              id="edit-location-city"
              name="edit-location-city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="edit-location-state">
              {t("manageLocations.editModal.stateLabel")}
            </label>
            <select
              id="edit-location-state"
              className={styles.select}
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="">{t("manageLocations.editModal.statePlaceholder")}</option>
              {[...US_STATES]
                .sort((a, b) => a.code.localeCompare(b.code))
                .map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code}
                  </option>
                ))}
            </select>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="edit-location-zip">
              {t("manageLocations.editModal.zipLabel")}
            </label>
            <Input
              id="edit-location-zip"
              name="edit-location-zip"
              type="text"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            variant="highlightTertiary"
            onClick={onClose}
            disabled={isSaving}
            style={{ fontWeight: 700 }}
          >
            {t("manageLocations.editModal.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving}
            style={{ fontWeight: 700 }}
          >
            {isSaving
              ? t("manageLocations.editModal.saving")
              : t("manageLocations.editModal.submit")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditLocationModal;
